import React from 'react';
import { Checkbox } from 'primereact/checkbox';
import { LdapGroupInfo } from 'shared';

interface GroupPickerProps {
  groups: LdapGroupInfo[];
  selected: string[];
  onChange: (groups: string[]) => void;
  disabled?: boolean;
}

/**
 * Two-section group selection: the organization groups (linienriss / hf-latte)
 * as prominent cards, all other role/function groups as a compact grid.
 * Used by both the create wizard and the edit dialog.
 */
export const GroupPicker: React.FC<GroupPickerProps> = ({
  groups,
  selected,
  onChange,
  disabled = false,
}) => {
  const orgGroups = groups.filter((g) => g.isOrgGroup);
  const roleGroups = groups.filter((g) => !g.isOrgGroup);

  const toggle = (cn: string) => {
    if (disabled) return;
    onChange(
      selected.includes(cn) ? selected.filter((g) => g !== cn) : [...selected, cn]
    );
  };

  return (
    <div data-component="ldap-group-picker" className="flex flex-col gap-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Organization</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {orgGroups.map((group) => {
            const checked = selected.includes(group.cn);
            return (
              <div
                key={group.cn}
                className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                  checked ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                onClick={() => toggle(group.cn)}
              >
                <div className="flex items-center gap-3">
                  <Checkbox checked={checked} disabled={disabled} readOnly />
                  <div>
                    <div className="font-medium">{group.cn}</div>
                    <div className="text-xs text-gray-500">
                      {group.description || 'Organization group'} · {group.memberCount}{' '}
                      {group.memberCount === 1 ? 'member' : 'members'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Roles / Functions</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {roleGroups.map((group) => {
            const checked = selected.includes(group.cn);
            return (
              <div
                key={group.cn}
                className={`flex items-center gap-2 border rounded p-2 cursor-pointer transition-colors ${
                  checked ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-400'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                onClick={() => toggle(group.cn)}
              >
                <Checkbox checked={checked} disabled={disabled} readOnly />
                <span className="text-sm">{group.cn}</span>
                <span className="text-xs text-gray-400 ml-auto">{group.memberCount}</span>
              </div>
            );
          })}
        </div>
        <small className="text-gray-500 mt-2 block">
          <i className="pi pi-info-circle mr-1" />
          Members of <strong>vorstand</strong> or <strong>it-admin</strong> can log in to
          MemberHub; role groups also affect access on the latte website.
        </small>
      </div>
    </div>
  );
};
